import { APP_INITIALIZER, CUSTOM_ELEMENTS_SCHEMA, NgModule, ErrorHandler, PLATFORM_ID, NgZone } from '@angular/core';
import { BrowserModule, HammerModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { FullRouterStateSerializer, RouterStateSerializer, StoreRouterConnectingModule } from '@ngrx/router-store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import {
  CustomSerializer,
  InitializationEffects,
  reducers,
  RouterEffects,
  StorageEffects,
  UiEffects
} from '@appstore/*';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { AppRoutingModule } from './app-routing.module';
import { OverviewModule } from './overview/overview.module';
import { CoreModule } from '@core/*';
import { actionSanitizer, SharedModule, stateSanitizer } from '@shared/*';
import { environment } from '@env/environment';
import { GalleryComponent, NotificationComponent, ToastbarComponent } from './components';
import { LegacyAppComponent } from './legacy-app.component';
import { AuthClientConfig, AuthHttpInterceptor, AuthModule } from '@auth0/auth0-angular';
import { defineCustomElements } from '@mateco/zenith-webcomponents/loader';
import { initializeAuth0Client } from './config/initialize-auth0-client';
import { AppComponent } from './app.component';
import { NewAppComponent } from './new-app.component';
import { APP_CONFIG } from './config/app-config';
import {  registerLocaleData } from '@angular/common';
import localeEN from '../../node_modules/@angular/common/locales/en.mjs';
import localeCS from '../../node_modules/@angular/common/locales/cs.mjs';
import localeHU from '../../node_modules/@angular/common/locales/hu.mjs';
import localeNL from '../../node_modules/@angular/common/locales/nl.mjs';
import localePl from '../../node_modules/@angular/common/locales/pl.mjs';
import localeES from '../../node_modules/@angular/common/locales/es.mjs';
import localeFR from '../../node_modules/@angular/common/locales/fr.mjs';
import localeDE from '../../node_modules/@angular/common/locales/de.mjs';
import { AcceptLanguageHeaderInterceptor } from './core/interceptors/accept-language-header.interceptor';
import * as Sentry from "@sentry/angular";
import { ErrorComponent } from './components/common/error-response/error.component';
import { firebaseServiceWorker, newFirebaseMessaging, newFirebaseMessagingInjectionToken } from './core/services/firebase.factory';
import { UserService } from './services/user.service';
import { SERVICE_WORKER } from '@angular/fire/compat/messaging';
import { ServiceWorkerService } from './core/services/service-worker.service';
import { provideZenithInspector } from '@mateco/zenith-webcomponents-angular/dist/zenith-webcomponents-angular';

[localeEN, localeCS, localeHU, localeNL, localePl, localeES, localeFR, localeDE].forEach(registerLocaleData);

@NgModule({ declarations: [AppComponent, LegacyAppComponent, ToastbarComponent, NotificationComponent, GalleryComponent],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    bootstrap: [AppComponent], imports: [AuthModule.forRoot( /* Config initialized via APP_INITIALIZER below. */),
        BrowserModule,
        HammerModule,
        BrowserAnimationsModule,
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: (http: HttpClient) => {
                    return new TranslateHttpLoader(http, './assets/i18n/', '.json');
                },
                deps: [HttpClient]
            }
        }),
        CoreModule,
        SharedModule,
        OverviewModule,
        AppRoutingModule,
        StoreModule.forRoot(reducers, {
            runtimeChecks: {
                strictStateImmutability: false,
                strictActionImmutability: false
            }
        }),
        EffectsModule.forRoot([RouterEffects, InitializationEffects, StorageEffects, UiEffects]),
        StoreRouterConnectingModule.forRoot({ serializer: FullRouterStateSerializer }),
        !environment.prodMode
            ? StoreDevtoolsModule.instrument({
                maxAge: 100,
                actionSanitizer,
                stateSanitizer
            })
            : [],
        NewAppComponent,
        ErrorComponent], providers: [
        {
            provide: APP_INITIALIZER,
            useFactory: initializeAuth0Client,
            deps: [AuthClientConfig, APP_CONFIG],
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: AuthHttpInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: AcceptLanguageHeaderInterceptor,
            multi: true
        },
        {
            provide: ErrorHandler,
            useValue: Sentry.createErrorHandler()
        },
        {
            provide: RouterStateSerializer,
            useClass: CustomSerializer
        },
        {
            provide: SERVICE_WORKER,
            deps: [UserService, ServiceWorkerService],
            useFactory: firebaseServiceWorker
        },
        {
            provide: newFirebaseMessagingInjectionToken,
            deps: [PLATFORM_ID, NgZone, SERVICE_WORKER],
            useFactory: newFirebaseMessaging
        },
        provideZenithInspector(!environment.prodMode),
        provideHttpClient(withInterceptorsFromDi()),
    ] })
export class AppModule {
  constructor() {
    defineCustomElements();
  }
}
